import type { CoachingSession } from '@efdigital/ll-addon-data';
import type CoachService from '@efdigital/ll-xceed-v2/services/coach';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import BsModal from 'ember-bootstrap/components/bs-modal';

interface CancelSessionModalSignature {
  Args: {
    session: CoachingSession;
    incursPenalty: boolean;
    onChanged?: () => void | Promise<void>;
  };
  Blocks: {
    default: [showModal: () => void];
  };
}

export class CancelSessionModal extends Component<CancelSessionModalSignature> {
  @service declare coach: CoachService;
  @tracked open = false;
  @tracked loading = false;
  @tracked cancelReason = '';
  @tracked errorMessage = '';

  closeCancelModal = () => {
    this.open = false;
  };

  get confirmButtonDisabled(): boolean {
    return this.cancelReason.trim().length === 0 || this.loading;
  }

  confirmCancel = async () => {
    const reason = this.cancelReason.trim();
    if (!reason) {
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    try {
      await this.coach.cancelSession(this.args.session, reason);
      await this.args.onChanged?.();
      this.open = false;
    } catch {
      this.errorMessage = 'Could not cancel this session. Please try again.';
    } finally {
      this.loading = false;
    }
  };

  setCancelReason = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.cancelReason = target.value;
  };

  showModal = () => {
    this.cancelReason = '';
    this.errorMessage = '';
    this.open = true;
  };

  <template>
    {{#if this.open}}
      <BsModal @open={{this.open}} as |modal|>
        <modal.header>
          <h5 class="modal-title">Cancel Session</h5>
        </modal.header>
        <modal.body aria-busy={{this.loading}}>
          <p>Are you sure you want to cancel this session?</p>
          {{#if @incursPenalty}}
            <p class="text-danger">You are cancelling this session at short
              notice.</p>
          {{/if}}

          <div class="mb-3">
            <label
              for="coach-cancelReason-{{@session.id}}"
              class="form-label"
            >Reason for cancellation</label>
            <input
              type="text"
              class="form-control"
              id="coach-cancelReason-{{@session.id}}"
              required
              value={{this.cancelReason}}
              {{on "input" this.setCancelReason}}
            />
          </div>
          {{#if this.errorMessage}}
            <div class="alert alert-danger mb-0" role="alert">
              {{this.errorMessage}}
            </div>
          {{/if}}
        </modal.body>
        <modal.footer>
          <button
            class="btn btn-outline-primary"
            type="button"
            {{on "click" this.closeCancelModal}}
            disabled={{this.loading}}
          >Do not cancel</button>
          <button
            class="btn btn-primary"
            type="button"
            {{on "click" this.confirmCancel}}
            disabled={{this.confirmButtonDisabled}}
            aria-busy={{this.loading}}
          >Confirm</button>
        </modal.footer>
      </BsModal>
    {{/if}}
    {{yield this.showModal}}
  </template>
}
